<template>
  <div :class="componentClass.root">
    <PmLoadingPure v-if="isLoading" size="default" />

    <PmKeyValueListPure v-else>
      <PmKeyValuePure v-if="IS_DEVELOPMENT" label="Id" variant="debug">
        {{ id }}
      </PmKeyValuePure>

      <PmKeyValuePure label="Bezeichnung">
        {{ name }}
      </PmKeyValuePure>

      <PmKeyValuePure label="Nummer">
        <PmPillPure :label="number" />
      </PmKeyValuePure>

      <PmKeyValuePure label="Status">
        <PmStatusPillPure
          v-if="status"
          type="purchaseOrder"
          :purchase-order-status="status.status"
          :label="status.label"
        />
      </PmKeyValuePure>

      <PmKeyValuePure v-if="orderedAt" label="Bestelldatum">
        {{ formatToRelativeDate(orderedAt) }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="type" label="Bestellart">
        {{ type }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="supplier" label="Lieferant">
        {{ supplier }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="supplierContact" label="Ansprechpartner">
        {{ supplierContact }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="supplierPhone" label="Telefon">
        {{ supplierPhone }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="supplierEmail" label="E-Mail">
        {{ supplierEmail }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="arrangedBy" label="Vorgangsleiter">
        {{ arrangedBy }}
      </PmKeyValuePure>

      <PmKeyValuePure label="Abgeschlossen">
        {{ isCleared ? 'Ja' : 'Nein' }}
      </PmKeyValuePure>

      <PmKeyValuePure label="Liefertermin">
        <template v-if="receivedAt">
          {{ formatToRelativeDate(receivedAt) }}
        </template>
      </PmKeyValuePure>

      <PmKeyValuePure label="Spätester Liefertermin">
        <template v-if="endAt">
          {{ formatToRelativeDate(endAt) }}
        </template>
      </PmKeyValuePure>

      <PmKeyValuePure v-if="paymentCondition" label="Zahlungsbedingung">
        {{ paymentCondition }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="totalPrice" label="Gesamtbetrag">
        {{ totalPrice }}
      </PmKeyValuePure>

      <PmKeyValuePure label="Ressourcen">
        <PmRichTextPure v-if="resources?.length">
          <div
            v-for="resource in resources"
            :key="resource.id"
            class="PmDataModalPurchaseOrderPure-resource"
          >
            <div class="PmDataModalPurchaseOrderPure-resourceTitle">
              <PmResourceShortPure
                v-if="resource.abbreviation"
                :interactive="false"
                :label="resource.abbreviation"
                :use-min-width="false"
              />

              <PmLinkPure
                v-if="resource.title"
                :label="resource.title"
                @click="emit('openResource', resource.id)"
              />

              <template v-if="resource.startDate && resource.endDate">
                {{ startEndDateForText(resource.startDate, resource.endDate) }}
              </template>
            </div>

            <ul>
              <li
                v-for="resourceAllocation in resource.resourceAllocations"
                :key="resourceAllocation.id"
              >
                <div class="PmDataModalPurchaseOrderPure-resourceAllocation">
                  <PmLinkPure
                    :label="resourceAllocation.title"
                    @click="
                      emit('openResourceAllocation', resourceAllocation.id)
                    "
                  />
                </div>
              </li>
            </ul>
          </div>
        </PmRichTextPure>
      </PmKeyValuePure>

      <PmKeyValuePure v-if="description" label="Beschreibung">
        {{ description }}
      </PmKeyValuePure>

      <PmKeyValuePure v-if="supplierNote" label="Hinweis Lieferant">
        {{ supplierNote }}
      </PmKeyValuePure>

      <PmKeyValuePure label="Erstellt">
        <template v-if="createdDate">
          {{ formatToRelativeDate(createdDate) }}
          <template v-if="createdBy"> von {{ createdBy }}</template>
        </template>
      </PmKeyValuePure>

      <PmKeyValuePure label="Zuletzt geändert">
        <template v-if="editedDate">
          {{ formatToRelativeDate(editedDate) }}
          <template v-if="editedBy"> von {{ editedBy }}</template>
        </template>
      </PmKeyValuePure>
    </PmKeyValueListPure>
  </div>
</template>

<script setup lang="ts">
import { useComponentClass } from '@thomasaull-shared/composables'

import { IS_DEVELOPMENT } from '@/constants/general'
import { formatToRelativeDate, startEndDateForText } from '@/utilities/date'

import PmKeyValueListPure from '@/components/basics/PmKeyValueList/PmKeyValueListPure.vue'
import PmKeyValuePure from '@/components/basics/PmKeyValueList/PmKeyValuePure.vue'
import PmLoadingPure from '@/components/basics/PmLoading/PmLoadingPure.vue'
import PmPillPure from '@/components/basics/PmPillPure.vue'
import PmLinkPure from '@/components/basics/PmLink/PmLinkPure.vue'
import PmRichTextPure from '@/components/basics/PmRichText/PmRichTextPure.vue'
import PmResourceShortPure from '@/components/persoplan/PmResource/PmResourceShortPure.vue'
import PmStatusPillPure from '@/components/persoplan/PmStatusPill/PmStatusPillPure.vue'

import type { PurchaseOrderStatus } from '@/composition/usePurchaseOrderStatus'
import type { Nilable } from '@/types/misc'

export interface Props {
  id: number
  isLoading?: boolean
  number?: Nilable<string>
  name?: Nilable<string>
  status?: {
    status: PurchaseOrderStatus
    label: string
  }
  orderedAt?: Nilable<Date>
  createdDate?: Nilable<Date>
  createdBy?: Nilable<string>
  editedDate?: Nilable<Date>
  editedBy?: Nilable<string>
  type?: Nilable<string>
  supplier?: Nilable<string>
  supplierContact?: Nilable<string>
  supplierPhone?: Nilable<string>
  supplierEmail?: Nilable<string>
  arrangedBy?: Nilable<string>
  isCleared?: Nilable<boolean>
  receivedAt?: Nilable<Date>
  endAt?: Nilable<Date>
  paymentCondition?: Nilable<string>
  totalPrice?: Nilable<string>
  description?: Nilable<string>
  supplierNote?: Nilable<string>
  resources?: Resource[]
}

export type Resource = {
  id: number
  title: Nilable<string>
  abbreviation: Nilable<string>
  startDate: Nilable<Date>
  endDate: Nilable<Date>
  resourceAllocations: ResourceAllocation[]
}

export type ResourceAllocation = {
  id: number
  title: string
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  (event: 'openResource', id: number): void
  (event: 'openResourceAllocation', id: number): void
}>()

const componentClass = useComponentClass()
</script>

<style lang="scss">
.PmDataModalPurchaseOrderPure {
  $block: &;

  &-resource {
    margin-bottom: var(--space-gutters);
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &-resourceTitle {
    display: flex;
    gap: 4px;
  }

  &-resourceAllocation {
    display: flex;
    gap: 4px;
  }
}
</style>
